import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const entities = [
  { v: "family_office", l: "Family Office" },
  { v: "private_office", l: "Private Office" },
  { v: "conglomerate", l: "Conglomerate" },
  { v: "advisor", l: "Advisor / Consultant" },
  { v: "lp_fund", l: "LP / Fund" },
  { v: "other", l: "Other" },
];

const regions = [
  { v: "uae", l: "UAE" },
  { v: "singapore", l: "Singapore" },
  { v: "india", l: "India" },
  { v: "eu", l: "European Union" },
  { v: "americas", l: "Americas (US · Canada)" },
  { v: "other", l: "Other" },
];

const mandates = [
  { v: "under_50m", l: "Under $50M" },
  { v: "50m_250m", l: "$50M — $250M" },
  { v: "250m_1b", l: "$250M — $1B" },
  { v: "over_1b", l: "Over $1B" },
  { v: "not_disclosed", l: "Prefer not to say" },
];

const initial = {
  name: "",
  email: "",
  role: "",
  entity_type: "",
  region: "",
  mandate_size: "",
  message: "",
};

export default function Workshop() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    // basic validation
    for (const k of ["name", "email", "role", "entity_type", "region", "mandate_size"]) {
      if (!form[k]) {
        toast.error("Please complete all required fields.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.message) delete payload.message;
      await axios.post(`${API}/workshop-requests`, payload);
      setDone(true);
      toast.success("Workshop request received. We'll be in touch within 48 hours.");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="workshop"
      className="relative py-24 md:py-40 border-t border-[var(--line)] bg-ink-2"
      data-testid="workshop-section"
    >
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-eyebrow uppercase text-gold mb-4">
              VIII. Engage
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Schedule a private strategy workshop.
            </h2>
            <p className="mt-6 text-ivory/70 max-w-md leading-relaxed">
              A confidential, 90-minute session with our partners. We map your
              mandate, sketch the capability center design, and quantify the
              90—120 day path.
            </p>
            <div className="hairline my-8 md:my-10 max-w-md" />
            <ul className="space-y-4 text-ivory/70 text-sm">
              <li className="flex gap-3">
                <span className="text-gold">01</span>
                <span>Mandate & principal-office alignment</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gold">02</span>
                <span>Module selection & TOM sketch</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gold">03</span>
                <span>Cost, timeline, and ROI framing</span>
              </li>
            </ul>
            <div className="mt-10 md:mt-14 text-[10px] tracking-eyebrow uppercase text-ivory-dim">
              Direct partner line
            </div>
            <a
              href="mailto:avadh@advikvc.com"
              data-testid="workshop-direct-email"
              className="font-display text-2xl md:text-3xl text-ivory gold-underline"
            >
              avadh@advikvc.com
            </a>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-gold/50 p-8 md:p-12 bg-ink/50 relative overflow-hidden"
                data-testid="workshop-success"
              >
                <div className="text-[10px] tracking-eyebrow uppercase text-emerald mb-4">
                  ● Received
                </div>
                <h3 className="font-display text-3xl md:text-4xl text-ivory leading-tight">
                  Thank you, {form.name.split(" ")[0]}.
                </h3>
                <p className="mt-4 text-ivory/70 max-w-md">
                  Your workshop request has been logged. A partner will reach
                  out at <span className="text-gold">{form.email}</span> within 48 hours to
                  confirm a mutually convenient window.
                </p>
                <button
                  onClick={() => {
                    setDone(false);
                    setForm(initial);
                  }}
                  data-testid="workshop-reset"
                  className="mt-8 text-sm text-ivory/70 hover:text-gold gold-underline"
                >
                  Submit another request
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={submit}
                className="border border-[var(--line)] p-6 md:p-10 bg-ink/40 relative"
                data-testid="workshop-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  <Field label="Full name" required>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={on("name")}
                      data-testid="input-name"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Work email" required>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={on("email")}
                      data-testid="input-email"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Role / title" required>
                    <input
                      type="text"
                      required
                      value={form.role}
                      onChange={on("role")}
                      data-testid="input-role"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Entity type" required>
                    <select
                      required
                      value={form.entity_type}
                      onChange={on("entity_type")}
                      data-testid="input-entity"
                      className={selectCls}
                    >
                      <option value="">Select…</option>
                      {entities.map((e) => (
                        <option key={e.v} value={e.v}>
                          {e.l}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Primary region" required>
                    <select
                      required
                      value={form.region}
                      onChange={on("region")}
                      data-testid="input-region"
                      className={selectCls}
                    >
                      <option value="">Select…</option>
                      {regions.map((r) => (
                        <option key={r.v} value={r.v}>
                          {r.l}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Mandate size" required>
                    <select
                      required
                      value={form.mandate_size}
                      onChange={on("mandate_size")}
                      data-testid="input-mandate"
                      className={selectCls}
                    >
                      <option value="">Select…</option>
                      {mandates.map((m) => (
                        <option key={m.v} value={m.v}>
                          {m.l}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="mt-6 md:mt-8">
                  <Field label="What would you like to explore?">
                    <textarea
                      value={form.message}
                      onChange={on("message")}
                      rows={4}
                      data-testid="input-message"
                      className={inputCls + " resize-none"}
                      placeholder="A few lines about the mandate, timing, or specific modules of interest."
                    />
                  </Field>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-[10px] tracking-eyebrow uppercase text-ivory-dim max-w-sm">
                    Confidential. No third-party sharing. Response within 48 hours.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="workshop-submit"
                    className="group inline-flex items-center gap-3 px-7 py-4 bg-gold text-ink font-medium tracking-wide hover:bg-gold-soft transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Submitting…" : "Request Workshop"}
                    <ArrowUpRight
                      size={18}
                      className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                    />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-transparent border-b border-[var(--line-strong)] focus:border-gold text-ivory text-base py-3 outline-none transition-colors placeholder:text-ivory-dim/60";
const selectCls =
  "w-full bg-ink border-b border-[var(--line-strong)] focus:border-gold text-ivory text-base py-3 outline-none transition-colors appearance-none pr-8";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-eyebrow uppercase text-ivory-dim mb-2">
        {label}
        {required && <span className="text-gold ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
